import React, { Component, createRef, forwardRef, RefObject, useEffect, useState } from 'react';
import { ReactThreeFiber } from 'react-three-fiber';
import anime, { AnimeInstance } from 'animejs';
import { Mesh } from 'three';
import { differenceWith, isEqual } from 'lodash';

type TStoryProgressBarItemProps = {
  width: number;
  height: number;
  position?: ReactThreeFiber.Vector3;

  duration: number;
  active: boolean;
  fill: boolean;

  onComplete: () => void;
};

class StoryProgressBarItem extends Component<TStoryProgressBarItemProps> {
  private animeInstance: AnimeInstance;
  private line: RefObject<Mesh>;
  private lineParams: {
    width: number;
    x: number;
  };

  constructor(props: TStoryProgressBarItemProps) {
    super(props);
    this.lineParams = {
      width: 0,
      x: -props.width / 2,
    };

    this.animeInstance = this.createAnimInstance();

    this.line = createRef();
  }

  componentDidMount() {
    this.updateLineState();
  }

  componentDidUpdate(prevProps: TStoryProgressBarItemProps) {
    const { active, fill } = this.props;
    const dif = differenceWith(
      [
        {
          active,
          fill,
        },
      ],
      [
        {
          fill: prevProps.fill,
          active: prevProps.active,
        },
      ],
      isEqual
    );

    if (dif.length) this.updateLineState();
  }

  componentWillUnmount() {
    // console.log('log: anim: pause animation');
    this.animeInstance.pause();
  }

  render() {
    const { width, height, position } = this.props;

    return (
      <group position={position}>
        <mesh renderOrder={2}>
          <planeBufferGeometry args={[width, height]} attach="geometry" />
          <meshBasicMaterial attach="material" color="#ffffff" transparent opacity={0.3} />
        </mesh>
        <mesh position={[0, 0, 0.001]} scale={[1, 1, 1]} renderOrder={3} ref={this.line}>
          <planeBufferGeometry args={[width, height]} attach="geometry" />
          <meshBasicMaterial attach="material" color="#ffffff" transparent opacity={1} />
        </mesh>
      </group>
    );
  }



  public statePlay = () => {
    this.animeInstance.play();
  };

  public statePause = () => {
    this.animeInstance.pause();
  };
  
  public stateReset = () => {
    this.updateLineState();
  };

  private updateLineState = () => {
    if (!this.line.current) return;

    const { active, fill } = this.props;
    const defaultPos: [number, number, number] = [0, 0, 0.001];

    this.animeInstance.pause();

    if (fill) {
      this.line.current.scale.set(1, 1, 1);
      this.line.current.position.set(...defaultPos);
    } else if (!active) {
      this.line.current.scale.set(0, 1, 1);
      this.line.current.position.set(...defaultPos);
    } else {
      // play animation
      this.animeInstance = this.createAnimInstance();
      this.animeInstance.play();
    }
  };


  private createAnimInstance = () => {
    const { duration, width } = this.props;

    this.lineParams = {
      width: 0,
      x: -width / 2,
    };

    const animeInstance = anime({
      targets: this.lineParams,
      /// animated props
      width: 1,
      x: 0,
      /// end animated props
      loop: false,
      autoplay: false,
      duration,
      easing: 'linear',
      update: this.animLineUpdate,
      complete: this.animLineComplete,
    });

    return animeInstance;
  };

  /// animation callbacks
  private animLineUpdate = () => {
    // console.log('log: anim:', this.lineParams);
    this.line.current?.scale.set(this.lineParams.width, 1, 1);
    this.line.current?.position.set(this.lineParams.x, 0, 0.001);
  };

  private animLineComplete = () => {
    // console.log('log: anim: complete');

    this.props.onComplete();
  };
}

// const StoryProgressBarItem = ({
//   width,
//   height,
//   position,

//   active,
//   fill,
// }: TStoryProgressBarItemProps) => {
//   const line = useRef<Mesh>();
//   const isActive = !fill && active;

//   useEffect(() => {
//     if (!line.current) return;

//     if (fill) {
//       line.current.scale.set(1, 1, 1);
//       line.current.position.set(1, 1, 0.001);
//       return;
//     }

//     const animeInstance = anime({

//       update:()=>{

//       }
//     })

//   }, [isActive, fill, line]);

//   return (
//     <group position={position}>
//       <mesh renderOrder={2}>
//         <planeBufferGeometry args={[width, height]} attach="geometry" />
//         <meshBasicMaterial attach="material" color="#ffffff" transparent opacity={0.3} />
//       </mesh>
//       <mesh position={[0, 0, 0.001]} scale={[1, 1, 1]} renderOrder={3} ref={line}>
//         <planeBufferGeometry args={[width, height]} attach="geometry" />
//         <meshBasicMaterial attach="material" color="#ffffff" transparent opacity={1} />
//       </mesh>
//     </group>
//   );
// };

type TStoryProgressBarProps = {
  width: number;

  count: number;
  active: number;
  duration: number;

  onComplete: () => void;
};

const spaceWidth = 0.01;

const StoryProgressBar = forwardRef(({ width, count, active, duration, onComplete }: TStoryProgressBarProps, ref) => {
  const [finished, setFinished] = useState<boolean>(false);
  const itemW = count > 0 ? (width - spaceWidth * (count - 1)) / count : width;
  let content: any[] = [];

  useEffect(() => {
    if (active !== count - 1) setFinished(false);
  }, [active, count]);

  for (let i = 0; i < count; i++) {
    const isActive = i === active;
    const isFill = i < active || finished;

    content.push(
      <StoryProgressBarItem
        key={i}
        width={itemW}
        height={0.004}
        position={[-(width / 2) + i * (itemW + spaceWidth) + itemW / 2, 0, 0]}
        fill={isFill}
        active={isActive}
        duration={duration}
        // @ts-ignore TODO: fix this
        ref={isActive ? ref : undefined}
        onComplete={() => {
          if (active === count - 1) setFinished(true);

          onComplete();
        }}
      />
    );
  }

  return <group>{content}</group>;
});

export default StoryProgressBar;
